// The Chrome extension's content script renders CorrectionPopup inside a Shadow DOM
// injected into the current webpage - not a Tauri window and not an Android WebView
// overlay, but the same "which platform am I in" branching problem the isAndroidOverlay
// pattern (see androidBridge.ts) already solves for Android. window.KalimatExtension is
// set by the content script (browser-extension/src/content/selection.tsx) right before
// mounting the popup, the same way FloatingOverlay.kt/CorrectionPopupOverlay.kt inject
// window.AndroidBridge via addJavascriptInterface() - a small bridge of native-ish calls
// the shared React components can use without caring which platform they're on.
//
// Unlike Android, the extension's popup runs in the SAME JS realm/localStorage as
// whatever arbitrary webpage it's injected into (Shadow DOM isolates the DOM/CSS, not
// storage or globals) - so, unlike androidBridge.ts, this bridge is also the reason
// apiConfig.ts/CorrectionPopup.tsx must never read the auth token from `localStorage`
// for the extension: that would read/write the HOST PAGE's own localStorage (a
// different, unrelated origin), not kalimatai.com's. See CorrectionPopup.tsx's
// getStoredToken() helper.
declare global {
  interface Window {
    KalimatExtension?: {
      /**
       * Writes text back into the page element (textarea/input/contentEditable) the
       * current selection came from - the extension's analogue of Android's
       * AndroidBridge.replace / desktop's replace_selected_text_command. See
       * textSelectionService.ts.
       */
      replace: (text: string) => void;
      /** Copies text to the system clipboard. */
      copy: (text: string) => void;
      /** Closes/unmounts the popup overlay injected into the page. */
      close: () => void;
      /**
       * Opens the kalimatai.com web app in a new tab. There's no separate main-window
       * process to switch to a Settings tab in like desktop has, so Settings/Refresh-config
       * just point the user at the real app instead.
       */
      openApp: () => void;
    };
  }
}

// Deliberately NOT `!!window.KalimatExtension` (a tempting parallel to isAndroidOverlay's
// `!!window.AndroidBridge`, and how this flag originally worked) - that bridge object is
// only assigned later, inside ExtensionOverlay.tsx's own useEffect, i.e. AFTER React has
// already mounted. But apiConfig.ts/CorrectionPopup.tsx read `isExtension` as a top-level
// module const, evaluated the instant those modules first load - which, for a content
// script's own bundle, happens BEFORE ExtensionOverlay's effect ever runs. Keying off
// window.KalimatExtension here would freeze isExtension at `false` forever (module-level
// const initializers run exactly once), silently breaking every isExtension branch in the
// app: apiConfig.ts would keep resolving the relative-web '/api' base URL instead of the
// absolute kalimatai.com one, and CorrectionPopup.tsx's Tauri-only effects (listen(),
// getCurrentWindow()) would never see their `if (isExtension) return;` guards trigger,
// throwing at mount ("Cannot read properties of undefined (reading 'transformCallback')")
// since no real Tauri runtime is present to answer those calls.
//
// chrome.runtime.id has none of that timing problem: Manifest V3 guarantees the
// `chrome.runtime` object is injected into a content script's isolated world before any
// of that content script's own code runs, so this is correct from the very first line of
// the very first module evaluated - no mount, no race. `declare const chrome: any` keeps
// this file's own type-check self-contained without requiring @types/chrome as a
// dependency of the main app (only browser-extension/'s own tsconfig has that).
declare const chrome: any;

export const isExtension =
  typeof window !== 'undefined' && typeof chrome !== 'undefined' && !!chrome?.runtime?.id;
